import { faTriangleExclamation } from '@fortawesome/pro-light-svg-icons';
import { faAdd } from '@fortawesome/pro-regular-svg-icons';
import { faUsers } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemAvatar from '@mui/material/ListItemAvatar';
import ListItemText from '@mui/material/ListItemText';
import Tooltip from '@mui/material/Tooltip';
import { useAtom } from 'jotai';
import { sortBy } from 'lodash';
import { useEffect, useMemo, useState } from 'react';

import CheckPermissions from '@/components/CheckPermissions';
import SearchableSelect from '@/components/SearchableSelect';
import UserAvatar from '@/components/UserAvatar';
import { PERMISSIONS } from '@/constants/auth.constants';
import { selectedOrganizationIdAtom } from '@/pages/Organizations';
import { mapWatchersDTO, ticketAtom } from '@/pages/Ticket';
import { DEFAULT_ORG_WATCHER } from '@/pages/Ticket/Ticket.constants';
import type { Watcher } from '@/pages/Ticket/Ticket.types';
import { displayIcon, getIconTooltipTitle } from '@/pages/Ticket/Ticket.utils';
import { isLayoutMobileAtom } from '@/store/layout';

import useStyles from './TicketWatchers.styles';
import type { TicketWatchersProps } from './TicketWatchers.types';
import { getDefaultOrgWatchers } from './TicketWatchers.utils';
import TicketWatchersAvatar from './TicketWatchersAvatar';

export const TicketWatchers = ({
  buttonIcon = false,
  buttonLabel = 'Add',
  disableOnCreateWithAllOrganizations = false,
  isTicketCreatePage = false,
  onChange,
  ticketPriority,
  ticketType,
  watchers,
  watchersOptions,
  ticketOrganizationId
}: TicketWatchersProps) => {
  // TODO: refactor to either use atom or props
  const [{ tags }] = useAtom(ticketAtom);
  const [isLayoutMobile] = useAtom(isLayoutMobileAtom);
  const [selectedOrganizationId] = useAtom(selectedOrganizationIdAtom);
  const [selectedWatchers, setSelectedWatchers] = useState<string[]>([]);
  const [previousPriority, setPreviousPriority] = useState<number | undefined>(
    undefined
  );
  const { classes } = useStyles({ isLayoutMobile });

  const DISABLE_DEFAULT_OPTION = ticketPriority?.toString() === '';

  //-----------------------------------------------------------------
  const mappedWatcherOptionsWithIconProps = useMemo(() => {
    const mappedWatcherOptions = mapWatchersDTO(watchersOptions);
    return mappedWatcherOptions.map((watcher) => {
      return {
        ...watcher,
        displayIcon: displayIcon(
          watcher,
          ticketPriority || 0,
          ticketType,
          tags
        ),
        iconTooltipTitle: getIconTooltipTitle(
          watcher,
          ticketPriority || 0,
          ticketType
        )
      };
    });
  }, [ticketPriority, ticketType, watchersOptions, tags]);

  const { filterOptions, orgWatchers } = useMemo(() => {
    const orgWatchers: Watcher[] = mappedWatcherOptionsWithIconProps.filter(
      (watcher) =>
        !watcher.internal ||
        watcher.organizationIds.includes(ticketOrganizationId || -1)
    ) as Watcher[];

    const orgWatcherIds = orgWatchers.map((watcher) => watcher.userId);

    const nonOrgWatchers = mappedWatcherOptionsWithIconProps.filter(
      (watcher) => !orgWatcherIds.includes(watcher.userId)
    );

    // set filterOptions
    const filterOptions: Watcher[] = [
      {
        ...DEFAULT_ORG_WATCHER,
        disable: DISABLE_DEFAULT_OPTION
      } as Watcher,
      ...sortBy(orgWatchers, ['label']),
      ...sortBy(nonOrgWatchers, ['label'])
    ];

    return {
      filterOptions,
      orgWatchers
    };
  }, [mappedWatcherOptionsWithIconProps, ticketOrganizationId]);

  const { defaultOrgWatchers, defaultOrgWatchersIds } = useMemo(() => {
    const defaultOrgWatchers: Watcher[] = getDefaultOrgWatchers(
      orgWatchers,
      ticketPriority || 0,
      ticketType,
      tags
    );
    const defaultOrgWatchersIds: string[] = defaultOrgWatchers.map(
      (watcher) => `${watcher.userId}`
    );

    return { defaultOrgWatchers, defaultOrgWatchersIds };
  }, [orgWatchers, tags]);

  const { ticketWatchers, ticketWatchersIds } = useMemo(() => {
    let ticketWatchers: Watcher[] = [];
    let ticketWatchersIds: string[] = [];
    const defaultOrgWatcherOptionSelected = defaultOrgWatchers.every(
      (defaultOrgWatcherOption) =>
        watchers.some(
          (watcher) => watcher.userId === defaultOrgWatcherOption.userId
        )
    );
    const filteredOptions = filterOptions.filter((option) =>
      watchers.some(
        (watcher) => watcher.userId === option.userId && option.userId !== 0
      )
    );
    const sortedFilteredOptions = sortBy(filteredOptions, [
      'internal',
      'label'
    ]);

    ticketWatchers = [
      ...(defaultOrgWatcherOptionSelected && defaultOrgWatchers.length > 0
        ? [DEFAULT_ORG_WATCHER]
        : []),
      ...sortedFilteredOptions
    ];

    ticketWatchersIds = ticketWatchers.map(
      (watcher: Watcher) => `${watcher.userId}`
    );

    return {
      ticketWatchers,
      ticketWatchersIds
    };
  }, [defaultOrgWatchers, filterOptions, watchers]);

  const disabled = useMemo(() => {
    return disableOnCreateWithAllOrganizations
      ? filterOptions.length === 0 || selectedOrganizationId === 0
      : filterOptions.length === 0;
  }, [
    disableOnCreateWithAllOrganizations,
    selectedOrganizationId,
    filterOptions
  ]);

  const startIcon = useMemo(() => {
    return buttonIcon ? <FontAwesomeIcon icon={faAdd} size="1x" /> : null;
  }, [buttonIcon]);

  useEffect(() => {
    setSelectedWatchers(ticketWatchersIds);
    setPreviousPriority(ticketPriority || 0);
  }, [ticketPriority, ticketWatchersIds]);

  useEffect(() => {
    // on update of priority, check if the default org watcher option is selected and add default org ticket watchers
    if (isTicketCreatePage) {
      const newDefaultOrgWatchers = getDefaultOrgWatchers(
        mappedWatcherOptionsWithIconProps,
        ticketPriority || 0,
        ticketType
      );

      const previousDefaultOrgWatchers = getDefaultOrgWatchers(
        mappedWatcherOptionsWithIconProps,
        previousPriority || 0,
        ticketType
      );

      const defaultOrgWatcherOptionSelected =
        previousDefaultOrgWatchers.length > 0 &&
        previousDefaultOrgWatchers.every((defaultOrgWatcherOption) =>
          ticketWatchers.some(
            (watcher) => watcher.userId === defaultOrgWatcherOption.userId
          )
        );

      if (defaultOrgWatcherOptionSelected) {
        const newWatchers = [...watchers, ...newDefaultOrgWatchers];
        const uniqueNewWatchers = newWatchers.filter(
          (obj, index, self) =>
            index === self.findIndex((item) => item.userId === obj.userId)
        );

        const ticketWatchersWithIconProps = uniqueNewWatchers.map((watcher) => {
          return {
            ...watcher,
            displayIcon: displayIcon(watcher, ticketPriority || 0, ticketType),
            iconTooltipTitle: getIconTooltipTitle(
              watcher,
              ticketPriority || 0,
              ticketType
            )
          };
        });

        onChange(ticketWatchersWithIconProps);
      }
    }
    // TODO: avoid setState in effect (derive or key-reset)
    setPreviousPriority(ticketPriority || 0);
  }, [defaultOrgWatchers, ticketPriority]);

  //-----------------------------------------------------------------
  const handleDefaultSelectWatchers = (
    key: string,
    selectedFilterOptions: string[]
  ) => {
    const selectedFilterOptionsIds = selectedFilterOptions.includes(key)
      ? selectedFilterOptions.filter((item) => item !== key)
      : [...selectedFilterOptions, key];

    // note: selectedFilterOptionsIds value has toggled the specified key from its previous state
    const allDefaultWatchersSelected = defaultOrgWatchersIds.every((value) =>
      selectedFilterOptionsIds.includes(value)
    );
    const isDefaultWatchersOptionSelected =
      selectedFilterOptionsIds.includes('0');
    let filteredSelectedFilterOptionsIds: string[];

    // case: creating ticket and no priority selected - disabled default org watchers option
    if (DISABLE_DEFAULT_OPTION) {
      filteredSelectedFilterOptionsIds = selectedFilterOptionsIds.filter(
        (value) => value !== '0'
      );
      setSelectedWatchers(filteredSelectedFilterOptionsIds);
      return;
    }

    // case: default org watchers option is selected
    if (isDefaultWatchersOptionSelected && key === '0') {
      filteredSelectedFilterOptionsIds = [
        ...new Set([...defaultOrgWatchersIds, ...selectedFilterOptionsIds])
      ];
      setSelectedWatchers(filteredSelectedFilterOptionsIds);
      return;
    }

    // case: default org watchers option is deselected
    if (!isDefaultWatchersOptionSelected && key === '0') {
      filteredSelectedFilterOptionsIds = selectedFilterOptionsIds.filter(
        (value) => !defaultOrgWatchersIds.includes(value)
      );
      setSelectedWatchers(filteredSelectedFilterOptionsIds);
      return;
    }

    // case: all default org watchers selected and default org watchers option is not selected
    if (allDefaultWatchersSelected && !isDefaultWatchersOptionSelected) {
      filteredSelectedFilterOptionsIds = [...selectedFilterOptionsIds, '0'];
      setSelectedWatchers(filteredSelectedFilterOptionsIds);
      return;
    }

    // case: all default org watchers not selected and default org watchers option is selected
    if (!allDefaultWatchersSelected && isDefaultWatchersOptionSelected) {
      filteredSelectedFilterOptionsIds = selectedFilterOptionsIds.filter(
        (value) => value !== '0'
      );
      setSelectedWatchers(filteredSelectedFilterOptionsIds);
      return;
    }

    // case: handle non-default org watcher options selected/deselected
    setSelectedWatchers(selectedFilterOptionsIds);
  };

  const handleSelectWatchers = (_key: string, selected: string[]) => {
    let selectedWatcherIds = [...selected];

    if (selectedWatcherIds.includes('0')) {
      selectedWatcherIds = [
        ...new Set([...selectedWatcherIds, ...defaultOrgWatchersIds])
      ];
    }

    const newWatchers: Watcher[] = [];
    selectedWatcherIds.forEach((selectedId) => {
      // use the existing watcher if it exists
      const watcher = ticketWatchers.find(
        (foundWatcher) => foundWatcher.userId === +selectedId
      );
      if (watcher) {
        newWatchers.push(watcher);
      } else {
        // otherwise, use the watcher option
        const watcherOption = filterOptions.find(
          (w) => w.userId === +selectedId
        );
        if (watcherOption) {
          newWatchers.push({ ...watcherOption, id: -1 });
        }
      }
    });

    onChange?.(newWatchers);
  };

  return (
    <>
      {ticketWatchers?.length > 0 && (
        <List className={classes.watchersList}>
          {ticketWatchers.map((watcher: Watcher) => (
            <ListItem key={watcher.userId} className={classes.watchersListItem}>
              <ListItemAvatar className={classes.watchersListItemAvatar}>
                <UserAvatar
                  name={watcher.label}
                  {...(watcher.userId === 0 && {
                    icon: <FontAwesomeIcon icon={faUsers} />
                  })}
                  {...(watcher.userId !== 0 && {
                    pondurance: watcher.internal
                  })}
                />
              </ListItemAvatar>
              <ListItemText
                className={
                  watcher.displayIcon
                    ? classes.watchersListItemTextWarning
                    : classes.watchersListItemText
                }
                primary={
                  <>
                    {watcher.label}
                    {` `}
                    {watcher.displayIcon && (
                      <Tooltip title={watcher.iconTooltipTitle}>
                        <FontAwesomeIcon
                          color="var(--mui-palette-Alert-warningIconColor)"
                          icon={faTriangleExclamation}
                          size="lg"
                        />
                      </Tooltip>
                    )}
                  </>
                }
                secondary={watcher.subLabel}
              />
            </ListItem>
          ))}
        </List>
      )}
      <CheckPermissions permissions={[PERMISSIONS.TICKETS_UPDATE]}>
        <SearchableSelect
          CustomAvatar={TicketWatchersAvatar}
          field="watchers"
          filterOptions={filterOptions}
          hasAvatars
          searchable
          showCounts={false}
          selectedOptions={selectedWatchers}
          titleButton="button"
          titleButtonProps={{
            label: buttonLabel,
            variant: 'outlined',
            disabled,
            startIcon
          }}
          onApply={handleSelectWatchers}
          onChange={handleDefaultSelectWatchers}
        />
      </CheckPermissions>
    </>
  );
};

TicketWatchers.displayName = 'TicketWatchers';

export default TicketWatchers;
